import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from "@jotty/ui";
import { ApiError, tenants } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/_authed/settings/general")({
  component: WorkspaceSettingsPage,
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function WorkspaceSettingsPage() {
  const tenantsList = useAuthStore((s) => s.tenants);
  const activeTenant = tenantsList[0];

  if (!activeTenant) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--color-text)]">
          Settings
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
          Manage your workspace preferences.
        </p>
      </div>

      <WorkspaceSection
        tenantId={activeTenant.id}
        initialName={activeTenant.name}
        initialBotDisplayName={activeTenant.bot_display_name}
        canEdit={activeTenant.role === "owner" || activeTenant.role === "admin"}
      />
      <LanguageSection tenantId={activeTenant.id} />
      <RetentionSection
        tenantId={activeTenant.id}
        initialRetentionDays={activeTenant.retention_days}
        canEdit={activeTenant.role === "owner" || activeTenant.role === "admin"}
      />
      <TeamSection tenantId={activeTenant.id} role={activeTenant.role} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace section — name + bot display name
// ---------------------------------------------------------------------------

function WorkspaceSection({
  tenantId,
  initialName,
  initialBotDisplayName,
  canEdit,
}: {
  tenantId: string;
  initialName: string;
  initialBotDisplayName: string;
  canEdit: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [botDisplayName, setBotDisplayName] = useState(initialBotDisplayName);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const persist = async (field: "name" | "bot_display_name", value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setError(null);
    setSaved(false);
    try {
      await tenants.update(tenantId, { [field]: trimmed });
      await useAuthStore.getState().refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void persist("name", name)}
            disabled={!canEdit}
          />
        </Field>
        <Field
          label="Bot display name"
          helper="Shown to meeting participants when the bot joins."
        >
          <Input
            value={botDisplayName}
            onChange={(e) => setBotDisplayName(e.target.value)}
            onBlur={() => void persist("bot_display_name", botDisplayName)}
            disabled={!canEdit}
          />
        </Field>
        {!canEdit ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Only owners and admins can edit workspace settings.
          </p>
        ) : null}
        {saved ? (
          <p className="text-xs text-[color:var(--color-success)]">Saved.</p>
        ) : null}
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

// // Language section
// ---------------------------------------------------------------------------

type Language = "id" | "en" | "bilingual";

function LanguageSection({ tenantId }: { tenantId: string }) {
  const [language, setLanguage] = useState<Language>("bilingual");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenants
      .getSettings(tenantId)
      .then((s) => {
        setLanguage(s.summary_language as Language);
      })
      .catch(() => {});
  }, [tenantId]);

  const handleChange = async (lang: Language) => {
    setLanguage(lang);
    setSaving(true);
    setError(null);
    try {
      await tenants.updateSettings(tenantId, { summary_language: lang });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Summary language</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Default language for AI-generated summaries. Can be overridden per
          meeting.
        </p>
        <div className="flex gap-2">
          {(["id", "en", "bilingual"] as Language[]).map((lang) => (
            <button
              key={lang}
              onClick={() => void handleChange(lang)}
              disabled={saving}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                language === lang
                  ? "bg-[color:var(--color-primary)] text-white"
                  : "border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
              }`}
            >
              {lang === "id"
                ? "🇮🇩 Indonesian"
                : lang === "en"
                  ? "🇬🇧 English"
                  : "🌐 Bilingual"}
            </button>
          ))}
        </div>
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Retention section
// ---------------------------------------------------------------------------

const RETENTION_OPTIONS = [30, 60, 90] as const;

function RetentionSection({
  tenantId,
  initialRetentionDays,
  canEdit,
}: {
  tenantId: string;
  initialRetentionDays: number;
  canEdit: boolean;
}) {
  const [retentionDays, setRetentionDays] = useState(initialRetentionDays);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (days: (typeof RETENTION_OPTIONS)[number]) => {
    setRetentionDays(days);
    setSaving(true);
    setError(null);
    try {
      await tenants.update(tenantId, { retention_days: days });
      await useAuthStore.getState().refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Retention</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <div>
          <p className="mb-2 text-sm text-[color:var(--color-text-muted)]">
            Recordings are deleted automatically after this many days.
          </p>
          <div className="flex gap-2">
            {RETENTION_OPTIONS.map((days) => (
              <button
                key={days}
                onClick={() => void handleChange(days)}
                disabled={saving || !canEdit}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  retentionDays === days
                    ? "bg-[color:var(--color-primary)] text-white"
                    : "border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
                }`}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-[color:var(--color-border)] pt-3 text-sm">
          <span className="text-[color:var(--color-text)]">
            Transcripts &amp; summaries
          </span>
          <span className="text-[color:var(--color-text-muted)]">
            Indefinite
          </span>
        </div>
        {!canEdit ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Only owners and admins can change retention.
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

// // Team section
// ---------------------------------------------------------------------------

interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
}

function TeamSection({ tenantId, role }: { tenantId: string; role?: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invited, setInvited] = useState(false);

  useEffect(() => {
    tenants
      .listMembers(tenantId)
      .then((res) => {
        setMembers(res.members ?? []);
      })
      .catch(() => {});
  }, [tenantId]);

  const isAdmin = role === "owner" || role === "admin";

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setError(null);
    setInvited(false);
    try {
      await tenants.createInvite(tenantId, {
        email: inviteEmail.trim(),
        role: "member",
      });
      setInvited(true);
      setInviteEmail("");
      setShowInvite(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send invite");
    } finally {
      setInviting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team</CardTitle>
        {isAdmin ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowInvite((v) => !v)}
          >
            {showInvite ? "Cancel" : "+ Invite teammate"}
          </Button>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-4">
        {showInvite ? (
          <form onSubmit={(e) => void handleInvite(e)} className="flex gap-2">
            <Field label="Email" className="flex-1">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@company.com"
                required
              />
            </Field>
            <div className="flex items-end">
              <Button type="submit" size="sm" disabled={inviting}>
                {inviting ? "Sending…" : "Send invite"}
              </Button>
            </div>
          </form>
        ) : null}
        {invited ? <Banner tone="success">Invite sent!</Banner> : null}
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
        {members.length > 0 ? (
          <ul className="divide-y divide-[color:var(--color-border)]">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-4 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-[color:var(--color-primary)]/10 text-[10px] font-bold text-[color:var(--color-primary)]">
                    {(m.name || m.email)[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[color:var(--color-text)]">
                      {m.name || m.email}
                    </p>
                    {m.name ? (
                      <p className="text-xs text-[color:var(--color-text-muted)]">
                        {m.email}
                      </p>
                    ) : null}
                  </div>
                </div>
                <span className="rounded-full border border-[color:var(--color-border)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--color-text-muted)]">
                  {m.role}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            No members yet.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
